<template>
    <auction-dialog title="낙찰 결과 통보서" width="1440" @open="init" @close="destroy">
        <template #activator="{attrs,on}">
            <slot name="activator" v-bind="{ attrs, on }" />
        </template>
        <template #default>
            <div class="mb-10 mb-md-14">
                <h3 class="tit tit--xs">
                    {{ hall }} <small class="pl-4">{{ program?.subject }}</small>
                </h3>
            </div>

            <v-data-table v-bind="{ headers, items }" disable-sort hide-default-footer mobile-breakpoint="768" class="v-data-table--default">
                <template v-for="header in headers.filter((header) => header.hasOwnProperty('formatter'))" #[`item.${header.value}`]="{ value }"> {{ header.formatter(value) }} </template>
                <template #[`item.work`]="{value}">
                    {{ value?.author?.name?.ko }} <br />
                    {{ value?.subject }}
                </template>
                <template #[`header.delivery`]="{ header }">
                    <div class="pa-10">{{ header.text }}</div>
                    <v-divider class="grey-da"></v-divider>
                    <v-row no-gutters>
                        <v-col class="pa-10">직접방문시</v-col>
                        <v-col cols="auto" class="d-none d-md-flex"><v-divider vertical class="grey-da"/></v-col>
                        <v-col class="pa-10">배송요청시</v-col>
                    </v-row>
                </template>
                <template #[`item.delivery`]>
                    <v-row no-gutters class="fill-height">
                        <v-col align-self="center" cols="12" md="">
                            <div class="d-md-none font-weight-medium">직접방문시</div>
                            KRW 0
                        </v-col>
                        <v-col cols="auto" class="d-none d-md-flex"><v-divider vertical class="grey-da"/></v-col>
                        <v-col align-self="center" cols="12" md="">
                            <div class="d-md-none font-weight-medium">배송요청시</div>
                            KRW {{ delivery }}
                        </v-col>
                    </v-row>
                </template>
                <v-divider class="grey-da"></v-divider>
                <template #footer>
                    <div class="pa-12 d-flex align-center justify-center grey-ef">
                        <span class="font-size-14 font-size-md-16 grey-68--text">
                            총 결제금액 : <span class="font-weight-medium primary--text"> KRW {{ totalCharge?.format?.() }}</span>
                        </span>
                    </div>
                    <v-divider class="grey-da"></v-divider>
                </template>
            </v-data-table>

            <div class="mt-16 mt-md-20">
                <v-card tile elevation="0" class="grey-ef">
                    <v-card-text class="pa-16 pa-md-24">
                        <p class="font-size-14 font-weight-bold line-height-16 grey-68--text">
                            ※ 대금 결제 및 배송 절차
                        </p>
                        <ul class="dot-list mt-4">
                            <li>낙찰된 작품은 낙찰을 철회할 수 없습니다. 기타 자세한 사항은 당사의 약관에 따릅니다.</li>
                            <li>낙찰자는 낙찰일로부터 7일 이내에, 낙찰가와 낙찰수수료, 부가가치세를 합친 구매대금을 아래의 입금계좌로 입금하셔야 합니다.</li>
                            <li>구매대금은 현금, 자기앞수표, 계좌이체 등으로 지급하실 수 있습니다.</li>
                            <li class="dashed-item">낙찰 수수료에 한해서는 신용카드 결제가 가능합니다</li>
                            <li class="dashed-item">신용카드로 결제하실 경우는 당사를 방문하셔야 가능합니다.</li>
                            <li>이체 시 낙찰자와 송금계좌 예금주가 다른 경우, 반드시 입금 통장 표시에 낙찰자명 기재 부탁 드립니다.</li>
                        </ul>
                        <p class="font-size-14 font-weight-bold line-height-16 grey-68--text pt-16">
                            ※ 입금계좌 : 우리은행 1002-752-126350 황규성
                        </p>
                    </v-card-text>
                </v-card>
            </div>
        </template>
    </auction-dialog>
</template>

<script>
import AuctionDialog from "./auction-dialog.vue";
import { AUCTION_PROGRAM_HALLS, DELIVERY_POLICIES_DOMESTIC } from "@/assets/variables";
const auctionHalls = Object.values(AUCTION_PROGRAM_HALLS);
const formatter = (value) => `KRW ${value?.format?.()}`;
const headers = [
    { divider: true, align: "center", text: "Lot", value: "work.lot" },
    { divider: true, align: "center", text: "작가/작품명", value: "work" },
    { divider: true, align: "center", text: "낙찰가 ⓐ", value: "work.price_auction.current", formatter },
    { divider: true, align: "center", text: "수수료 ⓑ", value: "work.price_auction.commission", formatter },
    { divider: true, align: "center", text: "VAT ⓒ", value: "work.price_auction.vat", formatter },
    { divider: true, align: "center", text: "낙찰대금 ⓐ+ⓑ+ⓒ", value: "work.price_auction.total", formatter },
    { divider: false, align: "center", text: "배송비 ⓓ", value: "delivery", class: "pa-0", cellClass: "pa-0" },
];

export default {
    components: {
        AuctionDialog,
    },
    props: {
        bids: { type: Array, default: () => [] },
        user: { type: Object, default: () => ({}) },
        program: { type: Object, default: () => ({}) },
    },
    data: () => ({
        headers,
    }),
    computed: {
        hall() {
            return auctionHalls.find(({ value }) => value == this.program?.hall)?.text;
        },
        items() {
            return this.bids;
        },
        delivery() {
            const { address1 = "" } = this.user || {};
            if (address1 == "") return 0;

            const policy = DELIVERY_POLICIES_DOMESTIC.find(({ test }) => test(address1));
            return policy?.value?.format?.();
        },
        totalCharge() {
            return this.bids.reduce((total, { work }) => total + +(work?.price_auction?.total || 0), 0);
        },
    },
    methods: {
        async init() {
            this.$emit("refresh");
        },
        async destroy() {},
    },
};
</script>

<style lang="scss" scoped>
.dashed-item::before {
    width: 4px;
    height: 1px;
}

@media (min-width: 600px) {
    ::v-deep table > tbody > tr > td:not(:first-child) {
        border-left: none !important;
    }
}
</style>
